'use client';

import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import clsx from 'clsx/lite';
import { useState } from 'react';
import { CaretIcon } from '~/components/Icons';
import { formatGameDate } from '~/utils/formatDate';
import { Container } from '../../Container';

const columnHelper = createColumnHelper();

const columns = [
  columnHelper.accessor('transactionDate', {
    header: 'DATE',
    cell: (info) => <div>{formatGameDate(info.getValue())}</div>,
    sortingFn: 'alphanumeric',
  }),
  columnHelper.accessor('team', {
    header: 'TEAM',
    cell: (info) => <div>{info.getValue()}</div>,
    sortingFn: 'alphanumeric',
  }),
  columnHelper.accessor('transaction', {
    header: 'TRANSACTION',
    cell: (info) => <div>{info.getValue()}</div>,
    sortingFn: 'alphanumeric',
  }),
];

// Custom sorting function
const customSortingFn = (rowA, rowB, columnId) => {
  const a = rowA.getValue(columnId);
  const b = rowB.getValue(columnId);

  // Special handling for gameDate
  if (columnId === 'gameDate') {
    const dateA = a ? new Date(a) : new Date(0);
    const dateB = b ? new Date(b) : new Date(0);
    return dateA - dateB; // Reverse chronological order
  }

  // Special handling for GP-GS
  if (columnId === 'gamesPlayedStarted') {
    return b - a; // Reverse numerical order
  }

  // Handle numeric values
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }

  // Handle string values
  return (a ?? '').toString().localeCompare((b ?? '').toString());
};

export function PlayerTransactionsTable({ data }) {
  const [sorting, setSorting] = useState([]);

  const table = useReactTable({
    data: data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    enableSorting: true,
    sortDescFirst: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <Container className="relative gap-6 py-5 lg:my-16">
      <div className="filter-wrap flex-row items-end justify-between gap-x-6 lg:flex">
        <div className="filter-title flex w-full flex-col justify-between lg:flex-row lg:items-center">
          <h2 className="shrink grow basis-0 font-nova text-4xl font-black uppercase text-black">
            Transactions
          </h2>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="data-table w-full">
          <thead className="bg-[--theme-secondary]">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={clsx(
                      'cursor-pointer select-none whitespace-nowrap px-6 py-3 font-nova text-lg font-semibold uppercase leading-tight tracking-wider text-[--theme-action]',
                      header.column.columnDef.header === 'Date' &&
                        'sticky left-0 z-10',
                    )}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div className="flex items-center gap-x-2">
                      {header.column.columnDef.header}
                      <CaretIcon
                        className={clsx(
                          'w-3',
                          !header.column.getIsSorted()
                            ? 'invisible'
                            : header.column.getIsSorted() === 'asc'
                              ? 'rotate-180'
                              : null,
                        )}
                      />
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="border-b-2 border-white bg-[#f0f0f0]">
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="whitespace-nowrap px-6 py-3 font-nova text-lg font-semibold leading-tight tracking-wider text-black"
                  >
                    {cell.column.columnDef.cell(cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Container>
  );
}

export default PlayerTransactionsTable;
